import axiosInstance from './axiosConfig';
import { API_BASE_URL } from './api';
console.log('🧩 StaffService - Base URL:', axiosInstance.defaults.baseURL);

// ----------------------
// Interfaces
// ----------------------
export interface StaffMember {
  id: number;
  name: string;
  email: string;
  phone: string;
  //role: string;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at: string;
}

// Custom error class
class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ----------------------
// Get All Staff Members
// ----------------------
export const getStaffMembers = async (
  params?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }
): Promise<StaffMember[]> => {
  try {
    // Use GET request with query parameters
    const response = await fetch(`${API_BASE_URL}/staff/list`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              limit: 25,
              search: '',
              sp_status: '',
              email_verified: ''
            })
          });

    // Check if the response has the expected data structure
    if (response.ok) {
        const result = await response.json();
        console.log(`✅ Staff fetched successfully`);
        console.log('Raw response:', result);

        // Handle different response structures based on your API
        let staffData;
        if (result.data?.staff) {
          staffData = result.data.staff;
        } else if (result.data) {
          staffData = result.data;
        } else if (Array.isArray(result)) {
          staffData = result;
        } else {
          staffData = [];
        }

        if (Array.isArray(staffData)) {
          return staffData.map((user: any) => ({
            id: user.sp_id || user.id,
            name: user.sp_name || user.name,
            email: user.sp_email || user.email,
            phone: user.sp_mobile || user.mobile || user.phone,
            status: user.sp_status || user.status,
            user_type: user.user_type,
            created_at: user.created_at,
            updated_at: user.updated_at
          }));
        } else {
          console.log('No staff array found in response');
          return [];
        }
      } else {
        const errorText = await response.text();
        console.log(`❌ Get staff failed: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch staff: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Error fetching staff:`, error);
      throw new Error('Failed to fetch staff members');
    }
};


// ----------------------
// Assign Staff to Booking
// ----------------------
export const assignStaffToBooking = async (
  bookingId: number,
  staffId: number
): Promise<any> => {
  try {
    console.log(`🧾 Assigning staff ${staffId} to booking ${bookingId}`);

    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/assign-staff`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        staff_id: staffId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Failed to assign staff: ${response.status} - ${errorText}`);
      throw new Error(`Failed to assign staff to booking (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ Staff assigned successfully:', result);

    // Normalize and return unified response
    const bookingData = result.data?.booking || result.booking || {};
    return {
      success: true,
      message: result.message || 'Staff assigned successfully',
      booking_id: bookingData.booking_id || bookingId,
      staff_id: staffId,
      staff_name: result.data?.staff_name || result.staff_name || '',
      staff_mobile: result.data?.staff_mobile || result.staff_mobile || ''
    };
  } catch (error: any) {
    console.error('❌ Error assigning staff to booking:', error);
    throw new Error(error.message || 'Error assigning staff to booking');
  }
};

// Add this at the end of staffService.ts
export const staffAPI = {
  getStaffMembers,
  assignStaffToBooking
  // Add other API functions here
};